import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, effect, inject, viewChild } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { SentryFeedbackService } from '../../core/services/sentry-feedback.service';

/**
 * Toolbar button that opens Sentry's feedback form (#20919).
 *
 * Sentry runs with `autoInject: false`, so this is the app's own trigger: it hands its element to
 * {@link SentryFeedbackService.registerTrigger}, which both attaches Sentry's click handler and
 * counts the button as a reachable trigger. While one is mounted, the floating fallback button
 * stays away — see the service docs for the full rule.
 *
 * Renders nothing when the feedback widget is disabled for the environment.
 */
@Component({
  selector: 'app-feedback-trigger-button',
  imports: [MatButtonModule, MatIconModule, MatTooltipModule],
  template: `
    @if (feedback.enabled) {
      <button
        #trigger
        mat-icon-button
        type="button"
        matTooltip="Feedback geven"
        aria-label="Feedback geven"
        data-cy="feedback-trigger-button">
        <mat-icon svgIcon="heroicons_outline:megaphone"></mat-icon>
      </button>
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FeedbackTriggerButtonComponent {
  protected readonly feedback = inject(SentryFeedbackService);

  /**
   * `read: ElementRef` is load-bearing: the ref sits on a `mat-icon-button`, which IS a component,
   * so a bare `viewChild('trigger')` hands back the MatIconButton instance and `.nativeElement` is
   * silently undefined — Sentry then gets no element, the button does nothing, and the floating
   * fallback never stands down because no trigger was ever counted.
   *
   * Angular queries cannot live on ES-private (#) members (NG1053).
   */
  private readonly trigger = viewChild('trigger', { read: ElementRef<HTMLButtonElement> });

  #release: (() => void) | null = null;

  /** Re-registers if the button is ever re-created (the @if gate makes that possible). */
  readonly #attachToSentry = effect(() => {
    const el = this.trigger()?.nativeElement;
    this.#release?.();
    this.#release = el ? this.feedback.registerTrigger(el) : null;
  });

  constructor() {
    inject(DestroyRef).onDestroy(() => this.#release?.());
  }
}
